/**
 * The edit lock in the editor (0.97.0): the read-only banner ("Being edited by Elin since 10:42" with Ask to edit,
 * Take over or Edit here instead; Start editing once nobody holds it), why this tab turned read-only, and the
 * holder's Ask-to-edit notice (Hand over or Dismiss).
 */
import { Hand, Lock, LockOpen, Loader2, MonitorSmartphone, UserRoundCheck } from 'lucide-react';
import type { LockMode, LockState, LostNotice } from './useSequenceLock';

/** "10:42", or "9 Oct 10:42" when it was not today. */
export function lockSince(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? time : `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
}

const lostText = (l: LostNotice) => {
  const unsaved = l.unsaved ? ' Your latest edits could not be saved.' : '';
  switch (l.kind) {
    case 'here': return `You are editing this sequence in another tab or on another computer now.${unsaved}`;
    case 'takeover': return `${l.by} took over editing. Your edits were saved.${l.unsaved ? ' Some of the latest could not be.' : ''}`;
    case 'handover': return `You handed over editing to ${l.by}.${unsaved}`;
    default: return `${l.by} is editing this sequence now.${unsaved}`;
  }
};

export function LockBanner({ mode, state, lost, asked, mayEdit, mayTakeOver, onStart, onAsk, onTakeOver }: {
  mode: LockMode; state: LockState | null; lost: LostNotice | null; asked: boolean; mayEdit: boolean; mayTakeOver: boolean;
  onStart: () => void; onAsk: () => void; onTakeOver: () => void;
}) {
  if (mode === 'editing' || mode === 'starting') return null;
  const lock = state?.lock;
  return (
    <div className="lock-bar" role="status" data-testid="lock-banner" data-holder={lock?.holder.displayName ?? ''}>
      {lost && <span className="lock-bar-lost" data-testid="lock-lost" data-kind={lost.kind}>{lostText(lost)}</span>}
      {mode === 'taking' ? (
        <span><Loader2 size={14} className="spin" /> Waiting for {lock?.holder.displayName ?? 'the editor'}’s edits to be saved…</span>
      ) : lock ? (
        <>
          <span className="lock-bar-text"><Lock size={14} /> {lock.sameUser ? `You are editing this sequence in another tab or on another computer (since ${lockSince(lock.since)})` : `Being edited by ${lock.holder.displayName} since ${lockSince(lock.since)}`}. Read only here.</span>
          {mayEdit && lock.sameUser && (
            <button className="btn sm primary" onClick={onTakeOver} data-testid="lock-edit-here"><MonitorSmartphone size={13} /> Edit here instead</button>
          )}
          {mayEdit && !lock.sameUser && (
            <button className="btn sm" onClick={onAsk} disabled={asked && !state?.request?.dismissed} data-testid="lock-ask">
              <Hand size={13} /> {asked && !state?.request?.dismissed ? 'Asked to edit' : 'Ask to edit'}
            </button>
          )}
          {mayEdit && mayTakeOver && !lock.sameUser && (
            <button className="btn sm danger" onClick={onTakeOver} data-testid="lock-take-over" title={`Save ${lock.holder.displayName}’s edits and continue editing here`}>
              <UserRoundCheck size={13} /> Take over
            </button>
          )}
        </>
      ) : (
        <>
          <span className="lock-bar-text"><LockOpen size={14} /> Nobody is editing this sequence now.{mayEdit ? '' : ' Read only.'}</span>
          {mayEdit && <button className="btn sm primary" onClick={onStart} data-testid="lock-start">Start editing</button>}
        </>
      )}
    </div>
  );
}

/** The holder's notice of an Ask to edit: stays until answered. */
export function LockRequest({ state, name, onAnswer }: { state: LockState | null; name: string; onAnswer: (a: 'handover' | 'dismiss') => void }) {
  const r = state?.request;
  if (!r || r.mine || r.dismissed || !state?.lock?.mine) return null;
  return (
    <div className="toasts lock-request-wrap">
      <div className="toast lock-request" role="alert" data-testid="lock-request">
        <Hand size={16} color="var(--accent)" />
        <span>{r.by.displayName} asks to edit “{name}”</span>
        <button className="btn sm primary" onClick={() => onAnswer('handover')} data-testid="lock-hand-over">Hand over</button>
        <button className="btn sm ghost" onClick={() => onAnswer('dismiss')} data-testid="lock-dismiss">Dismiss</button>
      </div>
    </div>
  );
}
